/**
 * Agent-facing visual workbook inspection.
 *
 * `get_snapshot` renders one bounded rectangular range as PNG and returns the
 * bytes out-of-band for provider-native vision input. Its textual result stays
 * compact so base64 never leaks into model-visible JSON, lifecycle events, or
 * Langfuse observations.
 */

import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import { getRangeSnapshot } from "./excelHostOps";
import {
  listWorksheetsMetadata,
  type WorksheetMetadata,
} from "./excelService";
import { parseA1Range } from "./executorRunSupport";
import {
  storeSnapshotPreview,
  type SnapshotPreviewInput,
} from "./snapshotPreviewStore";
import type { ToolCallDetailedResult } from "./toolRegistry";

/** Maximum cells accepted in one visual capture before the model must tile it. */
const MAX_SNAPSHOT_CELLS = 10_000;
/** Maximum decoded PNG payload accepted from a workbook host. */
const MAX_SNAPSHOT_BYTES = 5_000_000;
/** Cross-run task-pane queue that prevents overlapping clipboard-backed captures. */
let snapshotCaptureQueue: Promise<void> = Promise.resolve();

/** Strict provider-neutral definition of the agent-facing snapshot tool. */
export const GET_SNAPSHOT_TOOL: ChartCapabilityToolDef = {
  name: "get_snapshot",
  description:
    "Render one worksheet range as a PNG for visual inspection of layout, spacing, " +
    "alignment, colours, and presentation quality. This is not an authoritative source " +
    "for exact values, formulas, or number-format codes; use read_range for those. " +
    "Pass address=null to capture the worksheet's full used range. For a large sheet, " +
    "use list_sheets to discover that range, then cover it with multiple readable, " +
    "contiguous snapshots that retain headers or other visual context.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      sheetName: {
        type: "string",
        description: "Exact worksheet name to render.",
      },
      address: {
        type: ["string", "null"],
        description:
          "One contiguous A1 rectangle such as C2:N35, or null to render the used range.",
      },
      workbookName: {
        type: ["string", "null"],
        description:
          "Workbook name. Use null for the run's pinned workbook; the executor resolves it.",
      },
    },
    required: ["sheetName", "address", "workbookName"],
    additionalProperties: false,
  },
};

/** Validated arguments accepted by the snapshot executor. */
interface SnapshotToolInput {
  /** Worksheet requested by the model. */
  sheetName: string;
  /** Explicit bounded A1 range, or `null` for the worksheet used range. */
  address: string | null;
  /** Pinned workbook name, or `null` for the active run workbook. */
  workbookName: string | null;
}

/** Injectable workbook-host and preview-store operations used by the tool. */
export interface SnapshotToolDependencies {
  /** Lists workbook sheets and their used-range metadata. */
  listSheets: (workbookName?: string) => Promise<WorksheetMetadata[]>;
  /** Captures one absolute worksheet range and returns raw PNG base64. */
  capture: (address: {
    address: string;
    referenceType: "absolute";
    worksheetName: string;
    workbookName?: string;
  }) => Promise<string>;
  /** Optionally retains the validated image for a task-pane-only preview. */
  storePreview?: (input: SnapshotPreviewInput) => string;
}

/**
 * Run one host capture after every previously queued capture has settled.
 *
 * @param capture - Deferred host operation that returns raw snapshot base64.
 * @returns The queued capture result.
 */
function captureSerially(capture: () => Promise<string>): Promise<string> {
  const result = snapshotCaptureQueue.then(capture, capture);
  snapshotCaptureQueue = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

/** Production host dependencies for snapshot execution. */
const DEFAULT_DEPENDENCIES: SnapshotToolDependencies = {
  listSheets: listWorksheetsMetadata,
  capture: getRangeSnapshot,
  storePreview: storeSnapshotPreview,
};

/**
 * Validate, bound, and render one workbook range for provider-native vision.
 *
 * @param rawInput - Model-emitted `get_snapshot` arguments.
 * @param dependencies - Injectable host operations for focused tests.
 * @returns Compact JSON metadata plus one out-of-band PNG on success.
 */
export async function executeGetSnapshotTool(
  rawInput: unknown,
  dependencies: SnapshotToolDependencies = DEFAULT_DEPENDENCIES,
): Promise<ToolCallDetailedResult> {
  const parsed = parseInput(rawInput);
  if ("error" in parsed) return failure("invalid_snapshot_input", parsed.error);

  const { sheetName, workbookName } = parsed.value;
  let resolvedSheetName = sheetName;
  let address = parsed.value.address;
  if (address === null) {
    const sheets = await dependencies.listSheets(workbookName ?? undefined);
    const sheet = sheets.find(
      (candidate) =>
        candidate.name.localeCompare(sheetName, undefined, {
          sensitivity: "accent",
        }) === 0,
    );
    if (!sheet) {
      return failure(
        "snapshot_sheet_not_found",
        `Worksheet "${sheetName}" was not found.`,
        "Call list_sheets and retry with an exact worksheet name.",
      );
    }
    if (sheet.usedRange === null) {
      return failure(
        "snapshot_blank_sheet",
        `Worksheet "${sheet.name}" is blank and has no used range to render.`,
      );
    }
    if (!sheet.usedRange) {
      return failure(
        "snapshot_used_range_unknown",
        `The used range for worksheet "${sheet.name}" is unavailable.`,
        "Retry with one explicit bounded A1 rectangle.",
      );
    }
    resolvedSheetName = sheet.name;
    address = sheet.usedRange.address;
  }

  const bounded = validateBoundedRectangle(address);
  if ("error" in bounded) {
    return failure("invalid_snapshot_range", bounded.error, bounded.instruction);
  }

  const rawBase64 = await captureSerially(() =>
    dependencies.capture({
      address: bounded.address,
      referenceType: "absolute",
      worksheetName: resolvedSheetName,
      ...(workbookName ? { workbookName } : {}),
    }),
  );
  const base64 = normalizeBase64(rawBase64);
  if (!isValidBase64(base64)) {
    return failure(
      "snapshot_invalid_image",
      "The workbook host returned an invalid base64 snapshot.",
      "Retry the capture with a smaller range.",
    );
  }
  const byteLength = decodedByteLength(base64);
  if (byteLength <= 0) {
    return failure("snapshot_empty_image", "The workbook host returned an empty snapshot.");
  }
  if (byteLength > MAX_SNAPSHOT_BYTES) {
    return failure(
      "snapshot_image_too_large",
      `The rendered PNG is ${byteLength} bytes; the limit is ${MAX_SNAPSHOT_BYTES} bytes.`,
      "Retry with a smaller range.",
    );
  }
  const png = inspectPng(base64);
  if (!png) {
    return failure(
      "snapshot_invalid_png",
      "The workbook host response is not a complete PNG image with a valid header.",
      "Retry the capture with a smaller range.",
    );
  }

  let previewId: string | undefined;
  try {
    previewId = dependencies.storePreview?.({
      mimeType: "image/png",
      base64,
      sheetName: resolvedSheetName,
      address: bounded.address,
      byteLength,
      width: png.width,
      height: png.height,
    });
  } catch (error) {
    console.warn("[Excel] Snapshot preview could not be retained in task-pane memory.", {
      sheetName: resolvedSheetName,
      address: bounded.address,
      error: error instanceof Error ? error.message : String(error),
    });
  }

  return {
    output: JSON.stringify({
      ok: true,
      sheetName: resolvedSheetName,
      address: bounded.address,
      mimeType: "image/png",
      byteLength,
      width: png.width,
      height: png.height,
      imageAttached: true,
    }),
    observationMetadata: {
      snapshotSheetName: resolvedSheetName,
      snapshotAddress: bounded.address,
      snapshotByteLength: byteLength,
      snapshotWidth: png.width,
      snapshotHeight: png.height,
      imageAttached: true,
    },
    ...(previewId
      ? { displayMetadata: { snapshotPreviewId: previewId } }
      : {}),
    referenceImages: [
      {
        mimeType: "image/png",
        base64,
        description:
          `Visual snapshot of worksheet "${resolvedSheetName}" range ${bounded.address}. ` +
          "Use read_range to verify exact values, formulas, or number formats.",
      },
    ],
  };
}

/**
 * Validate and normalize model-emitted snapshot arguments.
 *
 * @param rawInput - Untrusted tool arguments emitted by the model.
 * @returns A normalized input object or a user-correctable validation error.
 */
function parseInput(
  rawInput: unknown,
): { value: SnapshotToolInput } | { error: string } {
  if (!rawInput || typeof rawInput !== "object" || Array.isArray(rawInput)) {
    return { error: "Arguments must be an object." };
  }
  const input = rawInput as Record<string, unknown>;
  const sheetName =
    typeof input.sheetName === "string" ? input.sheetName.trim() : "";
  if (!sheetName) return { error: "sheetName must be a non-empty string." };
  if (
    input.address !== null &&
    (typeof input.address !== "string" || !input.address.trim())
  ) {
    return { error: "address must be a non-empty A1 rectangle or null." };
  }
  if (
    input.workbookName !== null &&
    input.workbookName !== undefined &&
    typeof input.workbookName !== "string"
  ) {
    return { error: "workbookName must be a string or null." };
  }
  return {
    value: {
      sheetName,
      address:
        typeof input.address === "string" ? input.address.trim() : null,
      workbookName:
        typeof input.workbookName === "string" && input.workbookName.trim()
          ? input.workbookName.trim()
          : null,
    },
  };
}

/**
 * Validate a bare A1 rectangle and enforce the per-image cell budget.
 *
 * @param rawAddress - Candidate A1 address without a worksheet prefix.
 * @returns Normalized range metadata, or an actionable tiling instruction.
 */
function validateBoundedRectangle(
  rawAddress: string,
):
  | { address: string; cellCount: number }
  | { error: string; instruction?: string } {
  const address = rawAddress.trim().replace(/\$/g, "");
  if (address.includes(",") || address.includes("!")) {
    return {
      error: "address must be one bare, contiguous A1 rectangle without a sheet prefix.",
      instruction: "Pass the worksheet separately in sheetName.",
    };
  }
  const parsed = parseA1Range(address);
  if (!parsed || !/^[A-Z]+\d+(?::[A-Z]+\d+)?$/i.test(address)) {
    return {
      error: `"${rawAddress}" is not a bounded cell rectangle.`,
      instruction: "Use a range such as C2:N35; whole-row and whole-column ranges are not allowed.",
    };
  }
  const cellCount =
    (parsed.endRow - parsed.startRow + 1) *
    (parsed.endColumn - parsed.startColumn + 1);
  if (cellCount > MAX_SNAPSHOT_CELLS) {
    const rowCount = parsed.endRow - parsed.startRow + 1;
    const columnCount = parsed.endColumn - parsed.startColumn + 1;
    const rowsPerTile = Math.max(1, Math.floor(MAX_SNAPSHOT_CELLS / columnCount));
    const firstBandEndRow = Math.min(parsed.endRow, parsed.startRow + rowsPerTile - 1);
    const firstBand =
      `${columnLabel(parsed.startColumn)}${parsed.startRow + 1}:` +
      `${columnLabel(parsed.endColumn)}${firstBandEndRow + 1}`;
    const secondBandStartRow = firstBandEndRow + 1;
    const secondBand =
      secondBandStartRow <= parsed.endRow
        ? `${columnLabel(parsed.startColumn)}${secondBandStartRow + 1}:` +
          `${columnLabel(parsed.endColumn)}` +
          `${Math.min(parsed.endRow, secondBandStartRow + rowsPerTile - 1) + 1}`
        : undefined;
    return {
      error:
        `Range ${address} contains ${cellCount} cells (${rowCount} rows by ` +
        `${columnCount} columns); the limit is ${MAX_SNAPSHOT_CELLS}.`,
      instruction:
        columnCount <= MAX_SNAPSHOT_CELLS
          ? `Cover the full range with contiguous row bands of at most ${rowsPerTile} rows each ` +
            `(start with ${firstBand}${secondBand ? `, then ${secondBand}` : ""}), and repeat ` +
            "header rows when needed for visual context."
          : "Split the range into narrower contiguous column bands, then into row bands if needed.",
    };
  }
  return { address: address.toUpperCase(), cellCount };
}

/**
 * Convert a zero-based column index into an Excel column label.
 *
 * @param index - Zero-based worksheet column index.
 * @returns Uppercase Excel column label such as `A` or `AA`.
 */
function columnLabel(index: number): string {
  let value = index + 1;
  let label = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    value = Math.floor((value - 1) / 26);
  }
  return label;
}

/**
 * Remove an optional image data-URI prefix and insignificant whitespace.
 *
 * @param value - Raw host image response.
 * @returns Standard base64 suitable for validation and provider transport.
 */
function normalizeBase64(value: string): string {
  return value
    .trim()
    .replace(/^data:image\/(?:png|jpeg|webp|gif);base64,/i, "")
    .replace(/\s+/g, "");
}

/**
 * Check whether a string has valid standard-base64 syntax and padding.
 *
 * @param value - Normalized candidate base64.
 * @returns `true` when the string is syntactically valid and non-empty.
 */
function isValidBase64(value: string): boolean {
  return (
    value.length > 0 &&
    value.length % 4 === 0 &&
    /^[A-Za-z0-9+/]*={0,2}$/.test(value)
  );
}

/**
 * Calculate decoded byte length without allocating another image buffer.
 *
 * @param base64 - Valid standard base64.
 * @returns Number of decoded bytes represented by the input.
 */
function decodedByteLength(base64: string): number {
  if (!base64) return 0;
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

/**
 * Verify the PNG signature, required boundary chunks, and positive dimensions.
 *
 * @param base64 - Normalized base64 candidate returned by the workbook host.
 * @returns Image dimensions when the payload is a complete PNG; otherwise `undefined`.
 */
function inspectPng(base64: string): { width: number; height: number } | undefined {
  try {
    const binary = atob(base64);
    if (binary.length < 45) return undefined;
    const signature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
    if (signature.some((value, index) => binary.charCodeAt(index) !== value)) {
      return undefined;
    }
    /**
     * Read one unsigned big-endian 32-bit integer from the decoded PNG.
     *
     * @param offset - Byte offset within the decoded image string.
     * @returns Unsigned integer stored at the requested offset.
     */
    const uint32 = (offset: number) =>
      ((binary.charCodeAt(offset) << 24) |
        (binary.charCodeAt(offset + 1) << 16) |
        (binary.charCodeAt(offset + 2) << 8) |
        binary.charCodeAt(offset + 3)) >>> 0;
    if (uint32(8) !== 13 || binary.slice(12, 16) !== "IHDR") return undefined;
    const iendOffset = binary.length - 12;
    if (uint32(iendOffset) !== 0 || binary.slice(iendOffset + 4, iendOffset + 8) !== "IEND") {
      return undefined;
    }
    const width = uint32(16);
    const height = uint32(20);
    return width > 0 && height > 0 ? { width, height } : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Build a compact model-facing snapshot failure result.
 *
 * @param code - Stable machine-readable failure code.
 * @param error - Concise explanation of the failed operation.
 * @param instruction - Optional recovery instruction for the next model turn.
 * @returns Detailed tool result containing no image bytes.
 */
function failure(
  code: string,
  error: string,
  instruction?: string,
): ToolCallDetailedResult {
  return {
    output: JSON.stringify({
      ok: false,
      code,
      error,
      ...(instruction ? { instruction } : {}),
    }),
  };
}
